'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { CustomButton as Button } from '@/components/custom-button'
import { Slider } from '@/components/ui/slider'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
// @ts-ignore
import imageCompression from 'browser-image-compression'

interface ImageCropModalProps {
  image: string
  onCropComplete: (croppedImage: string, croppedFile: File) => void
  open: boolean
  onOpenChange: (open: boolean) => void

  aspect?: number | 'free'
  fileName?: string
  fileType?: string
  quality?: number
  maxSizeMB?: number
  maxWidthOrHeight?: number
  title?: string
  cancelText?: string
  applyText?: string
  skipText?: string
  zoomText?: string
  showZoom?: boolean
  showGrid?: boolean
  cropShape?: 'rect' | 'round'
  outputWidth?: number
  outputHeight?: number
  allowSkip?: boolean
  showAspectOptions?: boolean
  defaultAspectOptions?: Array<{ label: string; value: number | 'free' }>
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight,
  )
}

export const ImageCropModal = ({
  image,
  onCropComplete,
  open,
  onOpenChange,

  aspect = 16 / 9,
  fileName = 'cropped-image.jpg',
  fileType = 'image/jpeg',
  quality = 0.9,
  maxSizeMB = 1,
  maxWidthOrHeight = 1920,
  title = 'Crop & Insert Image',
  cancelText = 'Cancel',
  applyText = 'Apply',
  skipText = 'Use Original',
  zoomText = 'Zoom',
  showZoom = true,
  showGrid = true,
  cropShape = 'rect',
  outputWidth,
  outputHeight,
  allowSkip = true,
  showAspectOptions = true,
  defaultAspectOptions = [
    { label: 'Free', value: 'free' as const },
    { label: 'Square (1:1)', value: 1 },
    { label: 'Portrait (3:4)', value: 3 / 4 },
    { label: 'Landscape (4:3)', value: 4 / 3 },
    { label: 'Widescreen (16:9)', value: 16 / 9 },
  ],
}: ImageCropModalProps) => {
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [zoom, setZoom] = useState(1)
  const [containerWidth, setContainerWidth] = useState(0)
  const [currentAspect, setCurrentAspect] = useState<number | undefined>(
    aspect === 'free' ? undefined : (aspect as number)
  )
  const [isProcessing, setIsProcessing] = useState(false)

  // Reset state when modal opens with a new image
  useEffect(() => {
    if (open) {
      setZoom(1)
      setCrop(undefined)
      setCompletedCrop(undefined)
      // Measure container width after render
      setTimeout(() => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.clientWidth)
        }
      }, 50)
    } else {
      setIsProcessing(false)
    }
  }, [open])

  // Re-center crop when aspect changes
  const handleAspectChange = (value: number | 'free') => {
    const newAspect = value === 'free' ? undefined : (value as number)
    setCurrentAspect(newAspect)
    if (imgRef.current) {
      const { width, height } = imgRef.current
      if (newAspect) {
        setCrop(centerAspectCrop(width, height, newAspect))
      } else {
        setCrop({ unit: '%', width: 80, height: 80, x: 10, y: 10 })
      }
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget
    if (currentAspect) {
      setCrop(centerAspectCrop(width, height, currentAspect))
    } else {
      setCrop({ unit: '%', width: 80, height: 80, x: 10, y: 10 })
    }
  }

  const getCroppedImage = useCallback(async (): Promise<{ url: string; file: File }> => {
    if (!completedCrop || !imgRef.current) {
      return { url: image, file: new File([], fileName) }
    }

    const img = imgRef.current
    const scaleX = img.naturalWidth / img.width
    const scaleY = img.naturalHeight / img.height

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not get canvas context')

    const cropW = completedCrop.width * scaleX
    const cropH = completedCrop.height * scaleY

    canvas.width = outputWidth ?? cropW
    canvas.height = outputHeight ?? cropH

    ctx.drawImage(
      img,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      cropW,
      cropH,
      0,
      0,
      canvas.width,
      canvas.height,
    )

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), fileType, quality)
    )

    if (!blob) throw new Error('Failed to create blob from canvas')

    const file = new File([blob], fileName, { type: fileType })

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB,
        maxWidthOrHeight,
        useWebWorker: true,
      })
      return { url: URL.createObjectURL(compressed), file: compressed }
    } catch {
      return { url: URL.createObjectURL(blob), file }
    }
  }, [completedCrop, image, fileName, fileType, quality, maxSizeMB, maxWidthOrHeight, outputWidth, outputHeight])

  const handleApply = async () => {
    if (isProcessing) return
    setIsProcessing(true)
    try {
      const { url, file } = await getCroppedImage()
      onCropComplete(url, file)
      onOpenChange(false)
    } catch (e) {
      console.error('Error cropping image:', e)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleSkip = async () => {
    if (isProcessing) return
    setIsProcessing(true)
    try {
      const response = await fetch(image)
      const blob = await response.blob()
      const file = new File([blob], fileName, { type: fileType })
      try {
        const compressed = await imageCompression(file, { maxSizeMB, maxWidthOrHeight, useWebWorker: true })
        onCropComplete(image, compressed)
      } catch {
        onCropComplete(image, file)
      }
    } catch {
      onCropComplete(image, new File([], fileName))
    } finally {
      setIsProcessing(false)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] w-full z-[9999] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* Aspect Ratio Options */}
        {showAspectOptions && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {defaultAspectOptions.map((option) => (
              <button
                key={option.label}
                onClick={() => handleAspectChange(option.value)}
                className={`px-2 py-1.5 text-xs rounded-lg border transition-all ${
                  (option.value === 'free' && currentAspect === undefined) ||
                  (typeof option.value === 'number' && currentAspect === option.value)
                    ? 'bg-[#85C349] text-white border-[#85C349]'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#85C349] hover:text-[#85C349]'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {/* Crop area */}
        <div
          ref={containerRef}
          className="overflow-auto rounded-lg bg-gray-900"
          style={{ height: 'calc(90vh - 280px)', minHeight: '300px' }}
        >
          <ReactCrop
            crop={crop}
            onChange={(_, pct) => setCrop(pct)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={currentAspect}
            circularCrop={cropShape === 'round'}
            ruleOfThirds={showGrid}
            keepSelection
          >
            <img
              ref={imgRef}
              src={image}
              alt="Crop preview"
              onLoad={onImageLoad}
              style={{
                display: 'block',
                width: containerWidth ? `${containerWidth * zoom}px` : '100%',
                maxWidth: 'none',
              }}
            />
          </ReactCrop>
        </div>

        {/* Zoom */}
        {showZoom && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 w-10">1x</span>
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.05}
              onValueChange={([v]) => setZoom(v)}
              className="flex-1"
            />
            <span className="text-sm text-gray-500 w-10 text-right">{zoom.toFixed(1)}x</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-200">
          <div>
            {allowSkip && (
              <Button
                onClick={handleSkip}
                className="!bg-white !text-gray-600 hover:!bg-gray-50 !border-gray-300"
                disabled={isProcessing}
              >
                {skipText}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => onOpenChange(false)}
              className="!bg-white !text-gray-700 hover:!bg-gray-50 !border-gray-300"
              disabled={isProcessing}
            >
              {cancelText}
            </Button>
            <Button
              onClick={handleApply}
              className="bg-[#85C349] hover:bg-[#7AB33A] text-white font-medium px-6 disabled:opacity-50"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </div>
              ) : applyText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
